import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';

const MARKETING_PIXEL_SCRIPT_ATTR = 'data-marketing-pixel-script';

const isAnalyticsAllowed = () => {
  const consent = window.__cookieConsent;
  return !consent || consent.analytics === true;
};

const appendScript = ({ id, src, inlineContent }) => {
  if (document.getElementById(id)) return;

  const nonce = window.__CSP_NONCE__ || '';
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.setAttribute(MARKETING_PIXEL_SCRIPT_ATTR, 'true');

  if (nonce) {
    script.setAttribute('nonce', nonce);
  }

  if (src) {
    script.src = src;
  }

  if (inlineContent) {
    script.text = inlineContent;
  }

  document.head.appendChild(script);
};

const loadTradeDeskPixel = (ttdConfig) => {
  if (!ttdConfig?.advertiserId || !ttdConfig?.pixelId) return;

  window.ttdConversionEventsLayer = window.ttdConversionEventsLayer || [];
  window.ttdConversionEvents = function ttdConversionEvents() {
    window.ttdConversionEventsLayer.push(arguments);
  };

  appendScript({
    id: 'ttd-universal-pixel-script',
    src: 'https://js.adsrvr.org/up_loader.3.0.0.js'
  });

  if (window.__voxTtdPixelInitialized) return;

  window.ttdConversionEvents('init', {
    advertiserId: ttdConfig.advertiserId,
    pixelIds: [ttdConfig.pixelId]
  });

  window.ttdConversionEvents('event', {
    advertiserId: ttdConfig.advertiserId,
    pixelIds: [ttdConfig.pixelId]
  });

  window.__voxTtdPixelInitialized = true;
};

const loadGoogleAdsPixel = (googleAdsConfig) => {
  if (!googleAdsConfig?.id) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  appendScript({
    id: 'google-ads-pageview-script',
    src: `https://www.googletagmanager.com/gtag/js?id=${googleAdsConfig.id}`
  });

  if (window.__voxGoogleAdsPixelInitialized) return;

  window.gtag('js', new Date());
  window.gtag('config', googleAdsConfig.id);
  window.__voxGoogleAdsPixelInitialized = true;
};

const loadMetaPixel = (metaConfig) => {
  if (!metaConfig?.pixelId) return;

  if (!window.fbq) {
    const fbq = function fbq() {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };

    window.fbq = fbq;
    window._fbq = window._fbq || fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    appendScript({
      id: 'meta-pixel-script',
      src: 'https://connect.facebook.net/en_US/fbevents.js'
    });
  }

  if (window.__voxMetaPixelInitialized) return;

  window.fbq('init', metaConfig.pixelId);
  window.fbq('track', 'PageView');
  window.__voxMetaPixelInitialized = true;
};

const loadXPixel = (xConfig) => {
  if (!xConfig?.pixelId) return;

  if (!window.twq) {
    const twq = function twq() {
      twq.exe ? twq.exe.apply(twq, arguments) : twq.queue.push(arguments);
    };

    window.twq = twq;
    twq.version = '1.1';
    twq.queue = [];

    appendScript({
      id: 'x-pixel-script',
      src: 'https://static.ads-twitter.com/uwt.js'
    });
  }

  if (window.__voxXPixelInitialized) return;

  window.twq('config', xConfig.pixelId);
  window.__voxXPixelInitialized = true;
};

const loadMarketingPixels = (rtgConfig) => {
  if (!rtgConfig) return;
  loadTradeDeskPixel(rtgConfig.ttd);
  loadGoogleAdsPixel(rtgConfig.googleAds);
  loadMetaPixel(rtgConfig.meta);
  loadXPixel(rtgConfig.x);
};

const unloadMarketingPixels = () => {
  document.querySelectorAll(`script[${MARKETING_PIXEL_SCRIPT_ATTR}]`).forEach(script => {
    script.parentNode?.removeChild(script);
  });

  window.__voxTtdPixelInitialized = false;
  window.__voxGoogleAdsPixelInitialized = false;
  window.__voxMetaPixelInitialized = false;
  window.__voxXPixelInitialized = false;

  window.ttdConversionEventsLayer = window.ttdConversionEventsLayer || [];
  window.ttdConversionEvents = function ttdConversionEvents() {
    window.ttdConversionEventsLayer.push(arguments);
  };
  window.fbq = undefined;
  window._fbq = undefined;
  window.twq = undefined;
};

const isConversionPath = (pathname) => {
  const normalizedPath = pathname.replace(/\/+$/, '');

  return [
    /^\/[^/]+\/create-account$/,
    /^\/[^/]+\/signin$/,
    /^\/[^/]+\/gamification\/spinwheel(?:\/[^/]+)?$/
  ].some(pattern => pattern.test(normalizedPath));
};

const fireConversionPageView = (rtgConfig) => {
  if (!rtgConfig) return;

  if (window.gtag && rtgConfig.googleAds?.conversionSendTo) {
    window.gtag('event', 'conversion', {
      send_to: rtgConfig.googleAds.conversionSendTo
    });
  }

  if (window.fbq && rtgConfig.meta?.pixelId && rtgConfig.meta?.conversionEvent) {
    window.fbq('trackSingleCustom', rtgConfig.meta.pixelId, rtgConfig.meta.conversionEvent);
  }

  if (window.twq && rtgConfig.x?.conversionEventId) {
    window.twq('event', rtgConfig.x.conversionEventId, {});
  }
};

const MarketingPixelManager = () => {
  const location = useLocation();
  const firedConversionPaths = useRef(new Set());
  const { tenant } = useTenant();
  const rtgConfig = tenant?.tenantmasterconfig?.analytics?.rtg;

  useEffect(() => {
    const syncMarketingPixels = () => {
      if (isAnalyticsAllowed()) {
        loadMarketingPixels(rtgConfig);
      } else {
        unloadMarketingPixels();
      }
    };

    syncMarketingPixels();

    const handleConsentUpdate = () => {
      syncMarketingPixels();
    };

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate);
    return () => window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
  }, [rtgConfig]);

  useEffect(() => {
    if (!isAnalyticsAllowed() || !isConversionPath(location.pathname)) return;

    const conversionPath = `${location.pathname}${location.search}`;
    if (firedConversionPaths.current.has(conversionPath)) return;

    loadMarketingPixels(rtgConfig);
    fireConversionPageView(rtgConfig);
    firedConversionPaths.current.add(conversionPath);
  }, [location.pathname, location.search, rtgConfig]);

  return null;
};

export default MarketingPixelManager;
